import { formatMoney, parseDecimal } from "@bw/shared/utils"

import type { PricingRule } from "@/libs/ratesApi"
import { CHARGE_UNIT_LABELS, DAYS, MEMBERSHIP_SCOPE_LABELS, SERVICE_LABELS } from "./schema"

// Presentation helpers that humanize a pricing rule's conditions into plain
// English, mirroring roleGroupSummary's role in the staff/roles list. They
// DESCRIBE the rule (what it covers); they compute no price and make no
// eligibility decision — the backend owns all pricing logic.
//
// HONESTY: court_ids / membership_plan_ids are resolved to NAMES through the
// caller-supplied lookups; an id that no longer resolves (the court/plan was
// deleted while the rule still points at it) is DROPPED, never leaked as a raw
// UUID — the same resolve-or-drop discipline as the rule list's name columns.
// When EVERY id is unresolvable, a NEUTRAL placeholder is shown ("—" for courts,
// the scope label for plans) so the operator sees an honest "configured but
// stale" state rather than a blank-looks-broken cell or a leaked id.

const CHARGE_UNIT_SUFFIX: Record<string, string> = {
  per_hour: "/hr",
  per_session: "/session",
  flat: "",
}

// 0=Mon..6=Sun. Whole-week / weekday / weekend runs collapse to a friendly word
// so a common rule reads "Weekends" rather than "Sat, Sun".
function summarizeDays(days: number[]): string | null {
  if (!days.length) return null
  const sorted = [...new Set(days)].sort((a, b) => a - b)
  const key = sorted.join(",")
  if (key === "0,1,2,3,4,5,6") return "Every day"
  if (key === "0,1,2,3,4") return "Weekdays"
  if (key === "5,6") return "Weekends"
  return sorted
    .map((d) => DAYS.find((x) => x.value === d)?.label)
    .filter((l): l is string => Boolean(l))
    .join(", ")
}

export function recurrenceSummary(rule: PricingRule): string | null {
  switch (rule.repeat_type) {
    case "weekly": {
      const days = summarizeDays(rule.days_of_week ?? [])
      return days
    }
    case "daily":
      return "Every day"
    case "weekdays":
      return "Weekdays"
    case "weekends":
      return "Weekends"
    case "never":
    default:
      return null
  }
}

// The cadence + window clause — when the rule applies. all_day rules read "All
// hours"; a windowed rule reads "<cadence> · HH:MM–HH:MM". A one-off (repeat
// never) rule has no cadence word (it reads cleaner without "Never").
export function cadenceSummary(rule: PricingRule): string {
  const parts: string[] = []
  const recurrence = recurrenceSummary(rule)
  if (recurrence) parts.push(recurrence)
  if (rule.all_day) {
    parts.push("All hours")
  } else if (rule.start_time && rule.end_time) {
    parts.push(`${rule.start_time}–${rule.end_time}`)
  }
  // A windowless one-off rule still needs a non-empty label.
  return parts.length ? parts.join(" · ") : "Anytime"
}

// Who the rule covers — the membership scope. "any" is the implicit default, so
// it returns null (a rule that reads "Anyone" would be noise). specific_plans
// resolves to plan NAMES via the lookup; unresolvable ids are dropped and, when
// none resolve, fall back to the neutral scope label ("Specific plans").
export function scopeSummary(rule: PricingRule, planNameById: Map<string, string>): string | null {
  switch (rule.membership_scope) {
    case "any_member":
      return "Members"
    case "non_member":
      return "Non-members"
    case "specific_plans": {
      const names = (rule.membership_plan_ids ?? [])
        .map((id) => planNameById.get(id))
        .filter((n): n is string => Boolean(n))
      return names.length ? names.join(", ") : MEMBERSHIP_SCOPE_LABELS.specific_plans
    }
    case "any":
    default:
      return null
  }
}

// Where the rule applies — specific courts (resolved to NAMES; unresolvable ids
// dropped). No courts = facility-wide → null (no clause). When courts ARE
// configured but every id is stale, a neutral "—" placeholder is returned so the
// operator sees an honest stale state, never a raw UUID.
export function courtSummary(rule: PricingRule, courtNameById: Map<string, string>): string | null {
  const ids = rule.court_ids ?? []
  if (!ids.length) return null
  const names = ids.map((id) => courtNameById.get(id)).filter((n): n is string => Boolean(n))
  return names.length ? names.join(", ") : "—"
}

export function serviceSummary(rule: PricingRule): string {
  return SERVICE_LABELS[rule.service]
}

export function audienceSummary(rule: PricingRule, planNameById: Map<string, string>): string {
  return scopeSummary(rule, planNameById) ?? "Anyone"
}

export function targetSummary(
  rule: PricingRule,
  courtNameById: Map<string, string>,
  offeringNameById: Map<string, string>,
): string {
  if (rule.service === "classes") {
    return rule.offering_id ? (offeringNameById.get(rule.offering_id) ?? "—") : "—"
  }

  const courts = courtSummary(rule, courtNameById)
  const tags = (rule.court_tags ?? []).filter(Boolean)
  const targets = [courts, tags.length ? `Court tags: ${tags.join(", ")}` : null].filter((value): value is string =>
    Boolean(value),
  )

  if (targets.length) return targets.join(" · ")
  return rule.service === "court_reservation" ? "All courts" : "Facility-wide"
}

export function effectiveSummary(rule: PricingRule): string {
  if (rule.start_date && rule.end_date) {
    return `${rule.start_date} - ${rule.end_date}`
  }
  if (rule.start_date) return `From ${rule.start_date}`
  if (rule.end_date) return `Through ${rule.end_date}`
  return "Ongoing"
}

export function chargeUnitSummary(rule: PricingRule): string {
  return CHARGE_UNIT_LABELS[rule.charge_unit]
}

// The charge clause — base price (via formatMoney, never raw arithmetic) + the
// unit suffix. A $0 price reads "Free".
export function chargeSummary(rule: PricingRule): string {
  const isFree = parseDecimal(rule.base_price) === 0
  if (isFree) return "Free"
  const money = formatMoney(rule.base_price, rule.currency)
  const suffix = CHARGE_UNIT_SUFFIX[rule.charge_unit] ?? ""
  return `${money}${suffix}`
}

// The full one-line summary — cadence · who · where · charge — used as the rule
// row label when the operator did not name the rule. Example:
//   "Weekends · 09:00–21:00 · Members · Court 1 · $20.00/hr"
export function summarizeRule(
  rule: PricingRule,
  courtNameById: Map<string, string>,
  planNameById: Map<string, string>,
): string {
  const parts: string[] = [cadenceSummary(rule)]
  const who = scopeSummary(rule, planNameById)
  if (who) parts.push(who)
  const where = courtSummary(rule, courtNameById)
  if (where) parts.push(where)
  parts.push(chargeSummary(rule))
  return parts.join(" · ")
}
